import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import topics from "./topics";
import Subscribe from "./Subscribe";
import useTitle from "./useTitle";
import { trackPreview } from "./analytics";

const LEVEL_ZH = { s3: "中三", s4: "中四", s5: "中五", s6: "中六" };

const TopicPage = ({ level }) => {
    const { slug } = useParams();
    // Null until the reader asks for it: a chapter PDF is a few hundred KB and
    // most of this site's readers are on a phone, so it is not worth loading
    // one on every page view.
    const [preview, setPreview] = useState(null);
    const topic = topics.find((t) => t.level === level && t.slug === slug);
    useTitle(topic
        ? `${topic.zh}練習 ${topic.en} Exercise PDF｜${LEVEL_ZH[level]}數學`
        : "搵唔到呢個課題｜Math-EXE");

    if (!topic) {
        return (
            <div className="topic-page">
                <h1>搵唔到呢個課題</h1>
                <p>Topic not found.</p>
                <Link to={`/${level}`}>返回{LEVEL_ZH[level]}數學練習</Link>
            </div>
        );
    }

    const siblings = topics.filter((t) => t.level === level);
    const i = siblings.indexOf(topic);
    const prev = siblings[i - 1];
    const next = siblings[i + 1];
    const name = `${level}-ch${topic.ch}-${topic.slug}`;
    const base = `/pdf/${level}/${name}-ex1-`;

    const show = (lang) => {
        setPreview(preview === lang ? null : lang);
        if (preview !== lang) trackPreview(`${name}-${lang}`);
    };

    return (
        <div className="topic-page">
            <p className="crumb">
                <Link to="/">Home</Link> › <Link to={`/${level}`}>{LEVEL_ZH[level]}數學練習</Link> › Ch {topic.ch}
            </p>

            <h1>{topic.zh}練習（{LEVEL_ZH[level]}數學）</h1>
            <h2 className="topic-en">{topic.en} Exercise PDF — {level.toUpperCase()} Maths</h2>

            <p className="topic-intro">{topic.intro}</p>

            <div className="topic-preview-bar">
                <button type="button" className={preview === "chi" ? "on" : ""} onClick={() => show("chi")}>
                    {preview === "chi" ? "收起預覽" : "即睇中文版"}
                </button>
                <button type="button" className={preview === "eng" ? "on" : ""} onClick={() => show("eng")}>
                    {preview === "eng" ? "Hide preview" : "Preview English"}
                </button>
            </div>

            {preview && (
                <div className="topic-preview">
                    <iframe src={`${base}${preview}.pdf#view=FitH`}
                            title={`${topic.zh} ${topic.en} exercise preview`} />
                    <p className="topic-preview-note">
                        部分手機瀏覽器唔支援內嵌 PDF。睇唔到就撳下面個下載掣。
                    </p>
                </div>
            )}

            <div className="topic-download">
                <a href={`${base}chi.pdf`} target="_blank" rel="noreferrer">
                    下載練習 PDF（中文版）
                </a>
                <a href={`${base}eng.pdf`} target="_blank" rel="noreferrer">
                    Download Exercise PDF (English)
                </a>
            </div>

            <h3>重點公式 Key formulas</h3>
            <table className="topic-formulas">
                <tbody>
                    {topic.formulas.map(([label, body]) => (
                        <tr key={label}>
                            <th>{label}</th>
                            <td>{body}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <h3>常犯錯誤 Common mistakes</h3>
            <ul className="topic-mistakes">
                {topic.mistakes.map((m, n) => <li key={n}>{m}</li>)}
            </ul>

            <Subscribe where={`${level}-${topic.slug}`} />

            <h3>相關課題 Related topics</h3>
            <ul className="topic-related">
                {prev && <li><Link to={`/${level}/${prev.slug}`}>← Ch {prev.ch}：{prev.zh} {prev.en}</Link></li>}
                {next && <li><Link to={`/${level}/${next.slug}`}>Ch {next.ch}：{next.zh} {next.en} →</Link></li>}
                <li><Link to={`/${level}`}>全部{LEVEL_ZH[level]}數學練習（{siblings.length} 個課題）</Link></li>
                <li><Link to={`/${level}/notes`}>{LEVEL_ZH[level]}數學筆記：公式總表＋常犯錯誤</Link></li>
            </ul>
        </div>
    );
}

export default TopicPage;
